import { useStoreState } from "pullstate";
import Store, { IStore } from "../../../utils/Store";
import Draggable from "react-draggable";
import { useState, useCallback, memo, useEffect, useRef } from "react";
import AwesomeIcon from "../../AwesomeIcon";
import { faWindowMaximize, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { WindowProps } from "@copilotkit/react-ui";
import getEnv from "../../../utils/getEnv";
import { get } from "lodash";
import { useRouter } from "next/router";
import { useAuth } from "@qure/qure-auth";
const NODE_ENV = getEnv("NODE_ENV");
const isProdEnv = NODE_ENV === "production";
const DraggableModal = memo(({ children }: WindowProps) => {
  const { userInfo } = useAuth();
  const { isDraggableModalOpen, isDraggableModalMinimized, isDraggableModalMaximized } =
    useStoreState(Store, (s) => ({
      isDraggableModalOpen: s.isDraggableModalOpen,
      isDraggableModalMinimized: s.isDraggableModalMinimized,
      isDraggableModalMaximized: s.isDraggableModalMaximized,
    }));
  const modalRef = useRef<HTMLDivElement>(null);

  // get the isAIEnabled from userInfo
  const isAIAssistantEnabled = get(
    userInfo,
    ["settings", "ui_settings", "ai_assistant"],
    false
  );
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsMobile(window.matchMedia("(max-width: 639px)").matches);
    }
  }, []);
  const router = useRouter();

  useEffect(() => {
    if (isMobile) {
      // On mobile, open modal if assistant=open in URL
      const shouldOpen = router.query.assistant === "open";
      Store.update((s: IStore) => {
        s.isDraggableModalOpen = shouldOpen;
        s.isDraggableModalMaximized = shouldOpen;
        s.isDraggableModalMinimized = false;
      });
    } else {
      // Desktop: keep previous logic
      // Store.update((s: IStore) => {
      //   s.isDraggableModalOpen = !isProdEnv && isAIAssistantEnabled && !isMobile;
      // });
    }
    // Listen to router changes
  }, [isMobile, router.query.assistant, isAIAssistantEnabled]);

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragStop = useCallback((e: any, data: { x: number; y: number }) => {
    setIsDragging(false);
    setPosition({ x: data.x, y: data.y });
  }, []);

  const handleRestore = useCallback(() => {
    Store.update((s) => {
      s.isDraggableModalMinimized = false;
    });
    setPosition({ x: 0, y: 0 });
  }, []);

  if (!isDraggableModalOpen) return null;

  // On mobile, close should remove assistant param
  const handleCloseMobile = () => {
    const { assistant, ...rest } = router.query;
    router.replace({ pathname: router.pathname, query: rest }, undefined, {
      shallow: true,
    });
    Store.update((s: IStore) => {
      s.isDraggableModalOpen = false;
      s.isDraggableModalMaximized = false;
      s.isDraggableModalMinimized = false;
    });
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-50 flex ${
          isMobile ? "items-stretch justify-stretch" : "items-start justify-end"
        } pointer-events-none transition-opacity duration-300 ${
          isDraggableModalOpen ? "opacity-100" : "opacity-0"
        }`}
      >
        {isMobile ? (
          <div className="relative bg-white dark:bg-gray-900 w-full h-full m-0 overflow-auto flex flex-col pointer-events-auto will-change-transform transition-all duration-300 ease-in-out">
            {/* Header and children will be rendered by CopilotWrapper */}
            {/* Inject a close button for mobile if not present in header */}
            <div className="absolute top-4 right-4 z-10">
              <button
                onClick={handleCloseMobile}
                aria-label="Close"
                className="text-gray-700 hidden dark:text-gray-100 rounded-full p-2 shadow hover:bg-gray-300 dark:hover:bg-gray-700"
              >
                <AwesomeIcon name={faXmark} size="lg" />
              </button>
            </div>
            {/* Safe area padding wrapper for mobile content */}
            <div
              style={{
                paddingTop: "env(safe-area-inset-top, 1rem)",
                paddingBottom: "env(safe-area-inset-bottom, 1rem)",
              }}
              className="flex-1 flex flex-col"
            >
              {children}
            </div>
          </div>
        ) : (
          <Draggable
            handle=".drag-handle"
            onStart={handleDragStart}
            onStop={handleDragStop}
            grid={[50, 50]}
            position={isDraggableModalMaximized ? { x: 0, y: 0 } : position}
            disabled={isDraggableModalMaximized}
            bounds="parent"
          >
            <div
              ref={modalRef}
              className={`relative bg-white dark:bg-gray-900 rounded-lg shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[0_0_15px_rgba(0,0,0,0.3)] ${
                isDraggableModalMaximized
                  ? "w-full h-[100vh] m-0"
                  : "w-[calc(40%-2rem)] max-w-[calc(40%-2rem)] h-[98vh] mt-4 mr-4 border border-teal-500 dark:border-teal-400"
              } overflow-auto flex flex-col pointer-events-auto will-change-transform transition-all duration-300 ease-in-out ${
                !isDragging && "transition-transform duration-300 ease-in-out"
              } ${isDraggableModalOpen ? "translate-x-10" : "translate-x-full"} ${
                isDraggableModalMinimized ? "hidden" : ""
              }`}
            >
              <div
                className={`flex flex-col h-full pb-1 ${
                  isDraggableModalMaximized
                    ? "w-[70%] mx-auto border border-gray-200 dark:border-gray-800"
                    : ""
                }`}
              >
                {children}
              </div>
            </div>
          </Draggable>
        )}
      </div>
      {isDraggableModalMinimized && (
        <div className="fixed right-[calc(6rem)] top-[60px] z-[100]">
          <button
            onClick={handleRestore}
            className="bg-teal-500 hover:bg-teal-600 text-white py-3 px-5 rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.25)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.35)] transition-all duration-300 hover:scale-110 pointer-events-auto group flex items-center space-x-2"
            aria-label="Restore"
          >
            <AwesomeIcon name={faWindowMaximize} size="lg" className="text-white" />
            <span className="opacity-0 w-0 overflow-hidden group-hover:opacity-100 group-hover:w-auto transition-all duration-300 whitespace-nowrap">
              Restore
            </span>
          </button>
        </div>
      )}
    </>
  );
});

DraggableModal.displayName = "DraggableModal";

export default DraggableModal;
